// UW (Underwriting) Journey API Endpoints
// All UW-specific endpoints for the lending customer journey

import { type ApiType } from './endpoints';

const buildEndpoint = (path: string, apiType: ApiType = "customer"): string => {
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  if (apiType === "customer") {
    return `/iehub-customer/api/v1/${cleanPath}`;
  }
  return `/api/${cleanPath}`;
};

// UW Onboarding (batch save/retrieve)
export const uwOnboardingEndpoints = {
  getOnboarding: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}/onboarding`),
  saveOnboarding: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}/onboarding`),
  confirmOnboarding: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}/confirm-onboarding`),
};

// UW Application endpoints
export const uwApplicationEndpoints = {
  getApplication: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}`),
  getApplicationByCustomerId: (customerId: string | number) =>
    buildEndpoint(`uw/application/by-customer/${customerId}`),
  getApplicationByEmail: (email: string, customerId?: string | number) =>
    buildEndpoint(`uw/application/by-email/${encodeURIComponent(email)}${customerId ? `?customerId=${customerId}` : ''}`),
  getApplicationStatus: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}/status`),
  submitApplication: (applicationId: string) =>
    buildEndpoint(`uw/application/${applicationId}/submit`),
};

// UW Address History endpoints
export const uwAddressEndpoints = {
  getAddressHistory: (customerId: string | number) =>
    buildEndpoint(`uw/address-history/${customerId}`),
  saveAddressHistory: (customerId: string | number) =>
    buildEndpoint(`uw/address-history/${customerId}`),
};

// UW Consent endpoints
export const uwConsentEndpoints = {
  getConsents: (applicationId: string) =>
    buildEndpoint(`uw/consent/${applicationId}`),
  saveConsent: (applicationId: string) =>
    buildEndpoint(`uw/consent/${applicationId}`),
};

// UW Direct Debit endpoints
export const uwDirectDebitEndpoints = {
  getDirectDebit: (applicationId: string) =>
    buildEndpoint(`uw/direct-debit/${applicationId}`),
  saveDirectDebit: (applicationId: string) =>
    buildEndpoint(`uw/direct-debit/${applicationId}`),
};

// UW KYC Verification endpoints
export const uwKycEndpoints = {
  getVerification: (applicationId: string) =>
    buildEndpoint(`uw/kyc-verification/${applicationId}`),
  saveVerification: (applicationId: string) =>
    buildEndpoint(`uw/kyc-verification/${applicationId}`),
};

// UW Onfido KYC endpoints
export const uwOnfidoEndpoints = {
  initialize: (applicationId: string) =>
    buildEndpoint(`uw/kyc-onfido/initialize/${applicationId}`),
  status: (applicationId: string) =>
    buildEndpoint(`uw/kyc-onfido/status/${applicationId}`),
  reset: (applicationId: string) =>
    buildEndpoint(`uw/kyc-onfido/reset/${applicationId}`),
};

// UW Soft Check (LMX/Equifax Phase 1) endpoints
export const uwSoftCheckEndpoints = {
  start: (applicationId: string) =>
    buildEndpoint(`uw/soft-check/${applicationId}/start`),
  status: (applicationId: string) =>
    buildEndpoint(`uw/soft-check/${applicationId}/status`),
  // Admin/debug only: per-application summary for the Equifax tab.
  adminSummary: buildEndpoint('uw/soft-check-admin/applications'),
};

// UW Field Source tracking endpoints (read-only for clients; OB import writes server-side)
export const uwFieldSourceEndpoints = {
  getFieldSources: (applicationId: string) =>
    buildEndpoint(`uw/field-sources/${applicationId}`),
};

// UW Decision endpoint
export const uwDecisionEndpoints = {
  getDecision: (applicationId: string) =>
    buildEndpoint(`uw/decision/${applicationId}`),
};

// UW Summary / Affordability endpoint
export const uwSummaryEndpoints = {
  getSummary: (applicationId: string) =>
    buildEndpoint(`uw/summary/${applicationId}`),
};

// UW Open Banking endpoints
export const uwOBEndpoints = {
  initiate: buildEndpoint('uw/ob/initiate'),
  poll: buildEndpoint('uw/ob/poll'),
  complete: (applicationId: string) =>
    buildEndpoint(`uw/ob/complete/${applicationId}`),
  // Read-only: does this app have connected banks? No LMX call, no import.
  status: (applicationId: string) =>
    buildEndpoint(`uw/ob/status/${applicationId}`),
  confirmed: (applicationId: string) =>
    buildEndpoint(`uw/ob/confirmed/${applicationId}`),
  accounts: buildEndpoint('uw/ob/accounts'),
  transactionTotals: buildEndpoint('uw/ob/transaction-totals'),
  transactionsCsv: (params: { applicationId?: string; searchId?: string; accountNumber?: string; sortCode?: string }) => {
    const qs = new URLSearchParams();
    if (params.applicationId) qs.set('applicationId', params.applicationId);
    if (params.searchId) qs.set('searchId', params.searchId);
    if (params.accountNumber) qs.set('accountNumber', params.accountNumber);
    if (params.sortCode) qs.set('sortCode', params.sortCode);
    const query = qs.toString();
    return buildEndpoint(`uw/ob/transactions/csv${query ? `?${query}` : ''}`);
  },
  transactionsSummary: buildEndpoint('uw/ob/transactions/summary'),
  fieldComparisonCsv: (params: { applicationId: string }) => {
    const qs = new URLSearchParams();
    qs.set('applicationId', params.applicationId);
    return buildEndpoint(`uw/ob/field-comparison/csv?${qs.toString()}`);
  },
  discard: (applicationId: string) =>
    buildEndpoint(`uw/ob/discard/${applicationId}`),
  // Clears the active LMX search reference (lmxSearchId / lmxSearchInitiatedAt)
  // without touching imported data. Used to recover from a stuck poll.
  abandonSearch: (applicationId: string) =>
    buildEndpoint(`uw/ob/abandon-search/${applicationId}`),
};

// UW Magic Link auth endpoint
export const uwAuthEndpoints = {
  validateMagicLink: buildEndpoint("auth/uw-magic-link", "auth"),
};

// UW Webhook Log (debug) endpoints
export const uwWebhookLogEndpoints = {
  getLogs: (params?: { applicationId?: string; purpose?: string; purposes?: string; url?: string; status?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.applicationId) qs.set('applicationId', params.applicationId);
    if (params?.purpose) qs.set('purpose', params.purpose);
    if (params?.purposes) qs.set('purposes', params.purposes);
    if (params?.url) qs.set('url', params.url);
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return buildEndpoint(`uw/webhook-logs${query ? `?${query}` : ''}`);
  },
  retry: (logId: string) => buildEndpoint(`uw/webhook-logs/${logId}/retry`),
};

// UW admin/debug auth — verify the X-Admin-Key for the gated log viewers.
export const uwAdminEndpoints = {
  verifyKey: () => buildEndpoint('uw/admin/verify-key'),
};

// UW Invite Payload Log (inbound from CRM / external) endpoints
export const uwInviteLogEndpoints = {
  getLogs: (params?: {
    applicationId?: string;
    source?: string;
    resultStatus?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.applicationId) qs.set('applicationId', params.applicationId);
    if (params?.source) qs.set('source', params.source);
    if (params?.resultStatus) qs.set('resultStatus', params.resultStatus);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return buildEndpoint(`uw/invite-logs${query ? `?${query}` : ''}`);
  },
  getById: (logId: string) => buildEndpoint(`uw/invite-logs/${logId}`),
};

/** Documents hot-storage upload path; callers set HTTP `baseURL` (see UWService.uploadDocumentHotStorage). */
export const UW_DOCUMENTS_UPLOAD_HOT_STORAGE_PATH = "/api/Documents/upload?storage=hot";
